/// <amd-module name="Wit/WorkItemModel/RuleEngine/RuleEvaluator" />
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { TreeNodeStructureType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreField, FieldType } from "Wit/Common/Generated/Constants";
import { FieldRuleType, RuleName, RuleParameters } from "Wit/WorkItemDataManager/Contracts";
import { FieldStatusFlags, IEvalStateValue, IField, IItemGlobalValue, IItemGlobalValueList, IValueStatus, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { flagSorted } from "Wit/WorkItemModel/WorkItem/Sorting";
import { RuleEvaluatorExecutionPhase, ServerDefaultValueType } from "../OM";
import {
    compareFieldValues,
    convertFieldValueToInternal,
    getAllowedFieldValues,
    inList,
    intersect,
    isEmpty,
    isMatch,
    isRequired,
    union,
    unionLists
} from "../WorkItem/FieldUtils";
import { RuleValueFrom } from "./RuleValueFrom";
import { ServerDefaultValue } from "./ServerDefaultValue";
import { WorkItemIdentityScope } from "./WorkItemIdentityScope";

export const stateFieldID = 2;

export class RuleEvaluator {
    private static compareLists(listA: IItemGlobalValue[], listB: IItemGlobalValue[]): boolean {
        let l: number;
        let i: number;

        if (listA !== listB) {
            if (!listA || !listB) {
                return false;
            }

            l = listA.length;

            if (l !== listB.length) {
                return false;
            }

            for (i = 0; i < l; i++) {
                if (listA[i].items !== listB[i].items) {
                    return false;
                }

                if (listA[i].globals !== listB[i].globals) {
                    return false;
                }
            }
        }

        return true;
    }

    public static compareFieldLists(oldlists?: IItemGlobalValueList, newLists?: IItemGlobalValueList): boolean {
        if (oldlists !== newLists) {
            if (!oldlists || !newLists) {
                return false;
            }

            if (oldlists.allowExistingValue !== newLists.allowExistingValue) {
                return false;
            }

            if (!RuleEvaluator.compareLists(oldlists.allowedValues, newLists.allowedValues)) {
                return false;
            }

            if (!RuleEvaluator.compareLists(oldlists.suggestedValues, newLists.suggestedValues)) {
                return false;
            }

            if (!RuleEvaluator.compareLists(oldlists.prohibitedValues, newLists.prohibitedValues)) {
                return false;
            }
        }

        return true;
    }

    private workItem: IWorkItem;
    private fieldId: number;
    private field: IField;
    private value: any;
    private originalValue: any;
    private executionPhase: RuleEvaluatorExecutionPhase = RuleEvaluatorExecutionPhase.DefaultRules;
    private userChange: boolean = false;
    private valueLocked: boolean = false;
    private setByRule: boolean = false;
    private valueChanged: boolean = false; // whether rules have determined if this value should be updated (we are not comparing new value to old value though)
    private originalFieldStatus: FieldStatusFlags; // The original status of the field
    private setByDefaultRule: boolean = false;
    private setByComputedRule: boolean = false;
    public triggerFields: number[];
    private triggerFieldMap: { [key: string]: boolean };
    private allowedValues: IItemGlobalValue[];
    private suggestedValues: IItemGlobalValue[];
    private patterns: IItemGlobalValue[];
    public filterByScope: WorkItemIdentityScope;
    private prohibitedValues: IItemGlobalValue[];
    private readOnly: boolean = false;
    private empty: boolean = false;
    private required: boolean = false;
    private frozen: boolean = false;
    private cannotLoseValue: boolean = false;
    private otherFields: { [key: number]: any } = {};
    private otherFieldCheck: string;
    private allowExistingValue: boolean = false;
    private validUser: boolean = false;
    private serverDefault: boolean = false;
    private isIdentityField: boolean = false;
    private evalState: { [key: string]: IEvalStateValue };

    private whenCount = 0;

    constructor(
        fieldId: number,
        workItem: IWorkItem,
        field: IField,
        value: any,
        originalValue: any,
        valueLocked: boolean,
        evalState: { [key: string]: IEvalStateValue }
    ) {
        this.fieldId = fieldId;
        this.workItem = workItem;
        this.field = field;
        this.value = value;
        this.originalValue = originalValue;
        this.valueLocked = valueLocked;
        this.userChange = valueLocked;
        this.evalState = evalState;
        this.isIdentityField = field.fieldDefinition.isIdentity;
    }

    public evaluate(rules: FieldRuleType[]) {
        // add try catch so that work item form does not break. See incident 79525999 for more details
        try {
            if (rules) {
                this.setExecutionPhase(RuleEvaluatorExecutionPhase.CopyRules);
                this.Block(rules);

                this.setExecutionPhase(RuleEvaluatorExecutionPhase.DefaultRules);
                this.Block(rules);

                this.setExecutionPhase(RuleEvaluatorExecutionPhase.OtherRules);
                this.Block(rules);
            }

            this.validate();
        } catch (e) {
            const errorMessage = `Error occured in Rule Engine Evaluate. Name: ${e.Name} Message: ${e.message} Rules.length: ${
                rules.length
            } Rules.Information: ${rules.toString()} Stack: ${e.stack}`;
            const error = new Error(errorMessage);
            // TODO:Replat -- publish this?
            console.error(error);
        }
    }

    private setExecutionPhase(phase: RuleEvaluatorExecutionPhase) {
        this.executionPhase = phase;
    }

    private Block(rules: FieldRuleType[]) {
        let i = 0;
        const l = rules.length;
        while (i < l) {
            const ruleName = rules[i++] as RuleName;
            const ruleArguments = (rules[i++] as RuleParameters) || [];
            this[ruleName]?.apply(this, ruleArguments);
        }
    }

    /**
     * Store scoped data for the identity picker
     * @param constants strings to be used as identities
     * @param entityIds users and groups in scope
     * @param displayNames
     */
    private ScopedIdentity(
        constants: string[],
        entityIds?: string[],
        ancestorEntityIds?: string[],
        displayNames?: string[],
        excludeGroups?: boolean
    ) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            // Store value if not stored or current StoredIdentity rule is within a when clause. Last to write wins.
            if (this.whenCount > 0 || !this.filterByScope) {
                this.filterByScope = new WorkItemIdentityScope(ancestorEntityIds, entityIds, constants, displayNames, excludeGroups);
            }
        }
    }

    private BlockWhenChangedProhibitedValuesOnly(currentValue: any, rules: FieldRuleType[]) {
        let i = 0;
        const l = rules.length;
        while (i < l) {
            const ruleName = rules[i++] as RuleName;
            const ruleArguments = rules[i++] || ([] as RuleParameters);
            if (ruleName === "ProhibitedValues" && ruleArguments[0] && ruleArguments[0] instanceof Array) {
                const filteredArray = ruleArguments[0].filter(i => i !== currentValue);
                this.ProhibitedValues(filteredArray, ruleArguments[1]);
            }
        }
    }

    private When(inverse: boolean, fieldId: number, value: any, valueFrom: RuleValueFrom, block: FieldRuleType[]) {
        value = this._getValueFrom(valueFrom, value);
        const isIdentityField = this._isIdentityField(fieldId);
        const smartIdentityComparisonNeeded = valueFrom == RuleValueFrom.Value && isIdentityField;

        if (this._areValuesEqual(fieldId, this._getFieldValue(fieldId, false), value, true, smartIdentityComparisonNeeded) ? !inverse : inverse) {
            this.whenCount++;
            this.Block(block);
            this.whenCount--;
        }
    }

    private WhenWas(inverse: boolean, fieldId: number, value: any, valueFrom: RuleValueFrom, block: FieldRuleType[]) {
        value = this._getValueFrom(valueFrom, value);
        const isIdentityField = this._isIdentityField(fieldId);
        const smartIdentityComparisonNeeded = valueFrom == RuleValueFrom.Value && isIdentityField;

        if (this._areValuesEqual(fieldId, this._getFieldValue(fieldId, true), value, true, smartIdentityComparisonNeeded) ? !inverse : inverse) {
            this.whenCount++;
            this.Block(block);
            this.whenCount--;
        }
    }

    private WhenChanged(inverse: boolean, fieldId: number, block: FieldRuleType[]) {
        const valuesUnchanged: boolean = this._areValuesEqual(fieldId, this._getFieldValue(fieldId, false), this._getFieldValue(fieldId, true), true);
        if (valuesUnchanged ? inverse : !inverse) {
            this.whenCount++;
            this.Block(block);
            this.whenCount--;
        } else if (fieldId == stateFieldID && valuesUnchanged && !inverse) {
            this.whenCount++;
            this.BlockWhenChangedProhibitedValuesOnly(this._getFieldValue(fieldId, true), block);
            this.whenCount--;
        }
    }

    private WhenBecameNonEmpty(inverse: boolean, fieldId: number, block: FieldRuleType[]) {
        if (
            this._areValuesEqual(fieldId, this._getFieldValue(fieldId, true), null, true) &&
            !this._areValuesEqual(fieldId, this._getFieldValue(fieldId, false), null, true)
                ? !inverse
                : inverse
        ) {
            this.whenCount++;
            this.Block(block);
            this.whenCount--;
        }
    }

    private WhenRemainedNonEmpty(inverse: boolean, fieldId: number, block: FieldRuleType[]) {
        if (
            this._areValuesEqual(fieldId, this._getFieldValue(fieldId, true), null, true) ||
            this._areValuesEqual(fieldId, this._getFieldValue(fieldId, false), null, true)
                ? inverse
                : !inverse
        ) {
            this.whenCount++;
            this.Block(block);
            this.whenCount--;
        }
    }

    private OtherField(fieldId: number, check: string, originalValue?: boolean) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            const fieldValue = this._getFieldValue(fieldId, originalValue);
            // Take to consideration all fields under NOTSAMEAS rule. DTS TASK 1889746
            this.otherFields[fieldId] = fieldValue;
            this.otherFieldCheck = check;

            if (check === "same-as") {
                if (!this.userChange && originalValue) {
                    // Make True works for original values
                    this.value = fieldValue;
                    this.setByRule = true;
                    this.valueChanged = true;
                }
            }
        }
    }

    private Map(fieldId: number, inverse: boolean, cases: any, defaultCase: any) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.CopyRules) {
            const fieldValue = this._getFieldValue(fieldId, false);

            let values: any;

            if (!inverse) {
                if (!this.userChange) {
                    if (cases != null) {
                        for (const mapCase of cases) {
                            if (this._areValuesEqual(fieldId, fieldValue, mapCase.case, true)) {
                                values = mapCase;
                                break;
                            }
                        }
                    }

                    if (!values) {
                        values = defaultCase;
                    }

                    if (values) {
                        let valid = false;

                        if (values.values) {
                            for (const v of values.values) {
                                if (this._areValuesEqual(fieldId, v, this.value, true)) {
                                    valid = true;
                                    break;
                                }
                            }
                        }

                        if (!valid) {
                            if (!values.default) {
                                this.value = values.values && values.values[0];
                                this.valueLocked = true;
                                this.setByRule = true;
                                this.valueChanged = true;
                            } else if (values.values instanceof Array && values.values.includes(this.originalValue)) {
                                this.value = this.originalValue;
                                this.valueLocked = true;
                                this.setByRule = true;
                                this.valueChanged = true;
                            } else if (!this._areValuesEqual(fieldId, values.default, this.value, true)) {
                                this.value = values.default;
                                this.valueLocked = true;
                                this.setByRule = true;
                                this.valueChanged = true;
                            }
                        }
                    }
                }

                if (!this.allowedValues) {
                    this.allowedValues = [];
                }

                let allowedValues: string[] = [];

                if (cases != null) {
                    for (const caseValue of this._getAllowedValues(fieldId)) {
                        let values: any;

                        for (const mapCase of cases) {
                            if (this._areValuesEqual(fieldId, caseValue, mapCase.case, true)) {
                                values = mapCase;
                                break;
                            }
                        }

                        if (values) {
                            allowedValues = union(allowedValues, values.values, true);
                        }
                    }
                }

                if (defaultCase) {
                    allowedValues = union(allowedValues, defaultCase.values, true);
                }

                this.allowedValues.push({ items: allowedValues });
            } else {
                // handle inverse map case

                // check source field's prev value in this iteration
                const evalState = this.evalState[fieldId + ""];

                const prevValue = evalState && evalState.prevValue !== undefined ? evalState.prevValue : this._getFieldValue(fieldId, false);

                if (!this._areValuesEqual(fieldId, this._getFieldValue(fieldId, false), prevValue, true) && !this.userChange) {
                    let targetValue: any;
                    if (cases != null) {
                        for (const mapCase of cases) {
                            if (mapCase.values) {
                                for (const mapValue of mapCase.values) {
                                    if (this._areValuesEqual(fieldId, fieldValue, mapValue, true)) {
                                        targetValue = mapCase.case;
                                        break;
                                    }
                                }
                            }

                            if (targetValue) {
                                break;
                            }
                        }
                    }

                    if (targetValue !== undefined && !this._areValuesEqual(fieldId, targetValue, this.value, true)) {
                        this.value = targetValue;
                        this.valueLocked = true;
                        this.setByRule = true;
                        this.valueChanged = true;
                    }
                }
            }
        }
    }

    private AllowExistingValue() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.allowExistingValue = true;
        }
    }

    private ReadOnly() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.readOnly = true;

            if (!this.userChange) {
                // Make True
                this.value = this.originalValue;
                this.setByRule = true;
                this.valueChanged = true;
            }
        }
    }

    private Empty() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.empty = true;

            if (!this.userChange) {
                // Make True
                this.value = undefined;
                this.setByRule = true;
                this.valueChanged = true;
            }
        }
    }

    private Required() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.required = true;
        }
    }

    private Frozen() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.frozen = true;

            if (!this.userChange && this.originalValue) {
                // Make True
                this.value = this.originalValue;
                this.setByRule = true;
                this.valueChanged = true;
            }
        }
    }

    private CannotLoseValue() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.cannotLoseValue = true;
        }
    }

    private ValidUser() {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            this.validUser = true;
        }
    }

    private Copy(from: RuleValueFrom, value: any) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.CopyRules) {
            if (!this.valueLocked) {
                this.value = this._getValueFrom(from, value);
                this.valueLocked = true;
                this.setByRule = true;
                this.valueChanged = true;
            }
        }
    }

    private Default(from: RuleValueFrom, value: any) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.DefaultRules) {
            if (!this.valueLocked) {
                if (isEmpty(this.value)) {
                    this.value = this._getValueFrom(from, value);
                    this.valueLocked = true;
                    this.setByRule = true;
                    this.setByDefaultRule = true;
                    this.valueChanged = true;
                }
            }
        }
    }

    private AllowedValues(items: string[], globals: string[]) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            if (!this.allowedValues) {
                this.allowedValues = [];
            }

            if (items) {
                flagSorted(items, localeIgnoreCaseComparer);
            }

            this.allowedValues.push({ items: items, globals: globals });
        }
    }

    private SuggestedValues(items: string[], globals: string[]) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            if (!this.suggestedValues) {
                this.suggestedValues = [];
            }

            if (items) {
                flagSorted(items, localeIgnoreCaseComparer);
            }

            this.suggestedValues.push({ items: items, globals: globals });
        }
    }

    private ProhibitedValues(items: string[], globals: string[]) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            if (!this.prohibitedValues) {
                this.prohibitedValues = [];
            }

            if (items) {
                flagSorted(items, localeIgnoreCaseComparer);
            }

            this.prohibitedValues.push({ items: items, globals: globals });
        }
    }

    private Match(items: string[], globals: string[]) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            if (!this.patterns) {
                this.patterns = [];
            }

            if (items) {
                flagSorted(items, localeIgnoreCaseComparer);
            }

            this.patterns.push({ items: items, globals: globals });
        }
    }

    private ServerDefault(type: ServerDefaultValueType) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.CopyRules) {
            // Server default rule does not respect value lock.
            this.value = new ServerDefaultValue(type, this.originalValue);
            this.serverDefault = true;

            this.valueLocked = true;
            this.setByRule = true;
            this.setByDefaultRule = true;
            this.valueChanged = true;
        }
    }

    private Computed(fieldId: number) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.DefaultRules) {
            if (!this.valueLocked) {
                const computation = this.workItem.computeFieldValue(this.field.fieldDefinition.id, fieldId);

                if (computation && computation.success) {
                    this.value = computation.value;
                    this.valueLocked = true;
                    this.setByRule = true;
                    this.setByComputedRule = true;
                    this.valueChanged = true;
                }
            }
        }
    }

    private Trigger(fields: number[]) {
        if (this.executionPhase === RuleEvaluatorExecutionPhase.OtherRules) {
            if (fields) {
                if (!this.triggerFields) {
                    this.triggerFieldMap = {};
                    this.triggerFields = [];
                }

                fields.forEach((fieldId: number, i: number) => {
                    const fieldIdStr = fieldId + "";
                    if (!this.triggerFieldMap.hasOwnProperty(fieldIdStr)) {
                        this.triggerFieldMap[fieldIdStr] = true;
                        this.triggerFields.push(fieldId);
                    }
                });
            }
        }
    }

    /**
     * Stores the results in field.status and other members of field. field is the instance passed in through the constructor
     */
    private validate() {
        let value: any;
        const field: IField = this.field;
        this.originalFieldStatus = field.status;

        field.status = FieldStatusFlags.None;

        const isDateParsingFixEnabled = !isFeatureFlagEnabled(this.workItem.getPageContext(), FeatureFlags.DateParsingFixDisabled, false);

        const store = field.workItem.store;
        const translatedOriginalValue: IValueStatus = convertFieldValueToInternal(this.originalValue, field.fieldDefinition.type, isDateParsingFixEnabled);
        const translatedValue: IValueStatus = convertFieldValueToInternal(this.value, field.fieldDefinition.type, isDateParsingFixEnabled);
        value = translatedValue.value;
        field.status |= translatedValue.status;
        if (this.setByRule) {
            this.workItem.setFieldValueById(field.fieldDefinition.id, value, { setByRule: true });
            field.status |= FieldStatusFlags.SetByRule;
        }

        if (this.setByDefaultRule) {
            field.status |= FieldStatusFlags.SetByDefaultRule;
        }

        if (this.setByComputedRule) {
            field.status |= FieldStatusFlags.SetByComputedRule;
        }

        if (this.readOnly || this.serverDefault || this.empty) {
            field.status |= FieldStatusFlags.ReadOnly;
        }

        if (this.cannotLoseValue) {
            if (!isEmpty(this.originalValue)) {
                this.required = true;
            }
        }

        if (this.required) {
            field.status |= FieldStatusFlags.Required;
        }

        const lists = {} as IItemGlobalValueList;
        lists.allowedValues = this.allowedValues;

        if (this.allowedValues && this.allowedValues.length > 0) {
            field.status |= FieldStatusFlags.HasValues;
            field.status |= FieldStatusFlags.LimitedToValues;
        }

        lists.suggestedValues = this.suggestedValues;
        if (this.suggestedValues && this.suggestedValues.length > 0) {
            field.status |= FieldStatusFlags.HasValues;
            field.status &= ~FieldStatusFlags.LimitedToValues;
        }

        lists.prohibitedValues = this.prohibitedValues;
        lists.allowExistingValue = this.allowExistingValue;

        if (!RuleEvaluator.compareFieldLists(field.lists as IItemGlobalValueList, lists)) {
            field.lists = lists;
            field.allowedValues = undefined;
        }

        if (this.allowExistingValue) {
            field.status |= FieldStatusFlags.AllowsOldValue;
        }

        let patterns: string[] = [];
        if (this.patterns && this.patterns.length > 0) {
            patterns = unionLists(store, this.patterns[0].items, this.patterns[0].globals as string[]);
            for (const pattern of this.patterns) {
                patterns = intersect(patterns, unionLists(store, pattern.items, pattern.globals as string[]), true);
            }
        }

        // ScopedIdentities
        if (field.fieldDefinition.isIdentity) {
            field.filterByScope = this.filterByScope;
        }

        field.patterns = patterns;
        if (patterns.length > 0) {
            field.status |= FieldStatusFlags.HasFormats;
        }

        if (isEmpty(value)) {
            if (!this.empty) {
                if (isRequired(field)) {
                    field.status |= FieldStatusFlags.Required;
                    field.status |= FieldStatusFlags.InvalidEmpty;
                    return;
                }

                if (typeof this.otherFieldCheck !== "undefined") {
                    for (const otherFieldId in this.otherFields) {
                        if (isEmpty(this.otherFields[otherFieldId])) {
                            if (this.otherFieldCheck === "not-same-as") {
                                field.status |= FieldStatusFlags.Required;
                                field.status |= FieldStatusFlags.InvalidValueNotInOtherField;
                                return;
                            }
                        } else if (this.otherFieldCheck === "same-as") {
                            field.status |= FieldStatusFlags.Required;
                            field.status |= FieldStatusFlags.InvalidValueInOtherField;
                            return;
                        }
                    }
                }
            }
            return;
        }

        if (this.empty) {
            // field is marked as empty but value is not empty
            field.status &= ~FieldStatusFlags.ReadOnly;
            field.status |= FieldStatusFlags.InvalidNotEmpty;
            return;
        }

        if (
            this.readOnly &&
            !this.serverDefault &&
            !this.setByDefaultRule &&
            !this._areValuesEqual(field.fieldDefinition.id, value, translatedOriginalValue.value)
        ) {
            // field._status &= ~FieldStatusFlags.ReadOnly;
            field.status |= FieldStatusFlags.InvalidNotOldValue;
            return;
        }

        if (typeof this.otherFieldCheck !== "undefined") {
            for (const otherFieldId in this.otherFields) {
                if (this._areValuesEqual(field.fieldDefinition.id, value, this.otherFields[otherFieldId])) {
                    if (this.otherFieldCheck === "not-same-as") {
                        field.status |= FieldStatusFlags.InvalidValueInOtherField;
                        return;
                    }
                } else if (this.otherFieldCheck === "same-as") {
                    field.status |= FieldStatusFlags.InvalidValueNotInOtherField;
                    return;
                }
            }
        }

        if (this.frozen) {
            if (!isEmpty(translatedOriginalValue.value)) {
                if (!this._areValuesEqual(field.fieldDefinition.id, value, translatedOriginalValue.value)) {
                    field.status |= FieldStatusFlags.InvalidNotEmptyOrOldValue;
                    return;
                }
            }
        }

        // Project change explicitly sets values for area and iteration path and they should not be set as part of rules while project change is still in progress since the values for these
        // fields might still belong to the old project and they wont exist in the nodes cache for the new project.
        if (field.fieldDefinition.type === FieldType.TreePath && !this.workItem.isChangeProjectInProgress()) {
            const node = this.workItem.project.getNode(
                value,
                field.fieldDefinition.id === CoreField.AreaPath ? TreeNodeStructureType.Area : TreeNodeStructureType.Iteration
            );

            if (!node) {
                throw new Error("Node not found:" + value);
            }
        }

        if (!(this.serverDefault || value instanceof ServerDefaultValue)) {
            // text value is checked against patterns and lists
            // if value is server default bypass these checks

            const textValue = value === undefined || value === null ? "" : "" + value;

            if (field.patterns.length > 0) {
                let hasMatch = false;
                for (const pattern of field.patterns) {
                    if (isMatch(pattern, textValue)) {
                        hasMatch = true;
                        break;
                    }
                }

                if (!hasMatch) {
                    field.status |= FieldStatusFlags.InvalidFormat;
                    return;
                }
            }

            if (lists.prohibitedValues) {
                for (const prohibitedValue of lists.prohibitedValues) {
                    // It doesn't make sense require prohibited values to be specified with the exact case.
                    if (inList(store, textValue, prohibitedValue.items, prohibitedValue.globals as string[], true)) {
                        field.status |= FieldStatusFlags.InvalidListValue;
                        return;
                    }
                }
            }

            if (this.allowExistingValue && !isEmpty(translatedOriginalValue.value)) {
                if (this._areValuesEqual(field.fieldDefinition.id, value, translatedOriginalValue.value)) {
                    return;
                }
            }

            if (lists.allowedValues) {
                for (const allowedValue of lists.allowedValues) {
                    // Perform case insensitive search for the allowed values to make sure it matches exactly to the one in the lists.
                    if (!inList(store, textValue, allowedValue.items, allowedValue.globals as string[], true)) {
                        field.status |= FieldStatusFlags.InvalidListValue;
                        return;
                    }
                }
            }
        }
    }

    private _isIdentityField(fieldId: number) {
        const field = this.workItem.getFieldById(fieldId);
        return field && field.fieldDefinition.isIdentity;
    }

    private _getFieldValue(fieldId: number, originalValue?: boolean): any {
        if (fieldId === this.fieldId) {
            if (originalValue) {
                return this.originalValue;
            } else {
                return this.value;
            }
        } else {
            return this.workItem.getFieldValueById(fieldId, originalValue);
        }
    }

    private _getValueFrom(from: RuleValueFrom, value: any): any {
        switch (from) {
            case RuleValueFrom.Clock:
                return new Date();
            case RuleValueFrom.CurrentUser:
                return this.workItem.store.getCurrentUserName();
            case RuleValueFrom.OtherFieldOriginalValue:
                return this._getFieldValue(value as number, true);
            case RuleValueFrom.OtherFieldCurrentValue:
                return this._getFieldValue(value as number, false);
            case RuleValueFrom.Value:
                return value;
            case RuleValueFrom.CurrentValue:
                return this.value;
            case RuleValueFrom.OriginalValue:
                return this.originalValue;
            default:
                return value;
        }
    }

    private _areValuesEqual(fieldId: number, value1: any, value2: any, caseInsensitive?: boolean, identityComparisonNeeded?: boolean): boolean {
        // Try convert boolean field data to 0 or 1.
        // We need this for proper comparison as rule values are stored as "true" or "false", not 1 or 0.
        if (this.workItem.fieldMapById[fieldId] && this.workItem.fieldMapById[fieldId].fieldDefinition.type === FieldType.Boolean) {
            value1 = this._translateToBoolData(value1);
            value2 = this._translateToBoolData(value2);
        }

        if (this.workItem.fieldMapById[fieldId] && this.workItem.fieldMapById[fieldId].fieldDefinition.type === FieldType.Double) {
            value1 = this._removeTrailingStringDecimalZeroes(value1);
            value2 = this._removeTrailingStringDecimalZeroes(value2);
        }

        return compareFieldValues(value1, value2, caseInsensitive, identityComparisonNeeded);
    }

    private _removeTrailingStringDecimalZeroes(value: unknown): any {
        return typeof value === "string" ? value.replace(/(\.[1-9]*)0+$/g,"$1") : value;
    }

    private _translateToBoolData(value: any): any {
        return value && (value == 1 || String(value).toLowerCase() === "true") ? 1 : 0;
    }

    private _getAllowedValues(fieldId: number): string[] {
        const field: IField | undefined = this.workItem.getFieldById(fieldId);
        if (field) {
            return getAllowedFieldValues(field, this.workItem).value;
        } else {
            return [];
        }
    }
}
